import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, ViewEncapsulation, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';

interface RankRow {
  location: string;
  guests: number;
  nights: number;
  leadDays: number;
  airbnb: { position: number; page: number };
  vrbo: { position: number; page: number };
}

interface DateOption { value: string; label: string; rows: RankRow[]; }

@Component({
  selector: 'rank-tracker-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <form [formGroup]="filtersForm" class="rt-settings">
      <div class="rt-settings__filters">
        <!-- Address is driven by the page-level property picker (desktop header),
             so no per-card address select here. -->
        <tidy-select
          [label]="'Date'"
          [items]="dateItems"
          [form]="filtersForm.controls.date"
          formControlName="date"
          (optionChange)="onDateChange($event)" />
      </div>

      <div class="rt-settings__table">
        <div class="rt-settings__row rt-settings__row--head">
          <tidy-text [helper]="'Search query'" />
          <tidy-text [helper]="'Airbnb'" />
          <tidy-text [helper]="'Vrbo'" />
        </div>
        @for (r of currentRows; track r.location + r.guests + r.leadDays) {
          <div class="rt-settings__row">
            <div class="rt-settings__q">
              <tidy-text [body]="r.location" class="rt-settings__q-label bold" />
              <tidy-text [helper]="queryMeta(r)" class="rt-settings__q-meta" />
            </div>
            <div>
              <span class="rt-settings__badge"><tidy-text [body]="'#' + r.airbnb.position" /></span>
              <span class="rt-settings__page"><tidy-text [helper]="'p' + r.airbnb.page" /></span>
            </div>
            <div>
              <span class="rt-settings__badge"><tidy-text [body]="'#' + r.vrbo.position" /></span>
              <span class="rt-settings__page"><tidy-text [helper]="'p' + r.vrbo.page" /></span>
            </div>
          </div>
        }
      </div>
    </form>
  `,
  styles: [`
    .rt-settings { display: flex; flex-direction: column; gap: 14px; }

    .rt-settings__filters {
      display: grid;
      grid-template-columns: minmax(0, 280px) minmax(0, 240px);
      gap: 12px;
    }
    @media (max-width: 540px) {
      .rt-settings__filters { grid-template-columns: 1fr; }
    }

    .rt-settings__table {
      border: 1px solid var(--tidy-border-light);
      border-radius: 8px;
      overflow: hidden;
    }
    .rt-settings__row {
      display: grid;
      grid-template-columns: minmax(130px, 1.6fr) 1fr 1fr;
      align-items: center;
      padding: 10px 12px;
      border-top: 1px solid var(--tidy-border-light);
    }
    .rt-settings__row--head {
      border-top: 0;
      .helper-text {
        font-size: 11px !important;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--tidy-text-light) !important;
      }
    }
    .rt-settings__q { display: flex; flex-direction: column; gap: 2px; }
    .rt-settings__q-label .body-text { font-size: 14px; color: var(--tidy-text-primary); }
    .rt-settings__q-meta  .helper-text { font-size: 11px !important; color: var(--tidy-text-light) !important; }

    .rt-settings__badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: 10px;
      min-width: 28px;
      justify-content: center;
      background: var(--tidy-bg-hover);
      .body-text { font-weight: 600; color: var(--tidy-text-primary); font-variant-numeric: tabular-nums; }
    }
    .rt-settings__page {
      margin-left: 6px;
      .helper-text { font-size: 10px !important; color: var(--tidy-text-light) !important; }
    }
  `],
})
export class RankTrackerSettingsComponent {
  /** "2 guests · 3 nights · 14 days" — whole words so each unit can translate. */
  queryMeta(r: { guests: number; nights: number; leadDays: number }): string {
    const t = (text: string) => new TranslationPipe().transform(text);
    return `${r.guests} ${t('guests')} · ${r.nights} ${t('nights')} · ${r.leadDays} ${t('days')}`;
  }

  private readonly fb = inject(FormBuilder);

  selectedDate = '2026-06-07';

  readonly dates: DateOption[] = [
    {
      value: '2026-06-07',
      label: 'Sun, Jun 7, 2026',
      rows: [
        { location: 'Costa Mesa, CA',   guests: 2, nights: 3, leadDays: 14, airbnb: { position: 14, page: 1 }, vrbo: { position: 30, page: 2 } },
        { location: 'Costa Mesa, CA',   guests: 4, nights: 3, leadDays: 30, airbnb: { position: 17, page: 1 }, vrbo: { position: 22, page: 2 } },
        { location: 'Newport Beach, CA', guests: 2, nights: 3, leadDays: 14, airbnb: { position: 13, page: 1 }, vrbo: { position: 28, page: 2 } },
      ],
    },
    {
      value: '2026-06-21',
      label: 'Sun, Jun 21, 2026',
      rows: [
        { location: 'Costa Mesa, CA',   guests: 2, nights: 3, leadDays: 28, airbnb: { position: 9,  page: 1 }, vrbo: { position: 24, page: 2 } },
        { location: 'Costa Mesa, CA',   guests: 4, nights: 3, leadDays: 44, airbnb: { position: 12, page: 1 }, vrbo: { position: 19, page: 1 } },
        { location: 'Newport Beach, CA', guests: 2, nights: 3, leadDays: 28, airbnb: { position: 7,  page: 1 }, vrbo: { position: 21, page: 2 } },
      ],
    },
    {
      value: '2026-07-04',
      label: 'Sat, Jul 4, 2026',
      rows: [
        { location: 'Costa Mesa, CA',   guests: 2, nights: 3, leadDays: 41, airbnb: { position: 5,  page: 1 }, vrbo: { position: 15, page: 1 } },
        { location: 'Costa Mesa, CA',   guests: 4, nights: 3, leadDays: 57, airbnb: { position: 8,  page: 1 }, vrbo: { position: 12, page: 1 } },
        { location: 'Newport Beach, CA', guests: 2, nights: 3, leadDays: 41, airbnb: { position: 3,  page: 1 }, vrbo: { position: 9,  page: 1 } },
      ],
    },
  ];

  readonly dateItems: SelectModel[] = this.dates.map(d => ({ value: d.value, viewValue: d.label }));

  readonly filtersForm: FormGroup = this.fb.group({
    date: [this.selectedDate],
  });

  onDateChange(value: string): void {
    this.selectedDate = value;
  }

  get currentRows(): RankRow[] {
    return this.dates.find(d => d.value === this.selectedDate)?.rows ?? this.dates[0].rows;
  }
}
